import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listCrossAccountPatterns,
  previewPatternSeeds,
  seedAdminPatterns,
  setPatternAdminFlag,
} from "@/lib/cross-account-patterns.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/patterns")({
  component: AdminPatternsPage,
});

type PatternType =
  | "csv_mapping"
  | "product_classification"
  | "metric_override"
  | "threshold_tuning";

function AdminPatternsPage() {
  const list = useServerFn(listCrossAccountPatterns);
  const flag = useServerFn(setPatternAdminFlag);
  const qc = useQueryClient();
  const [type, setType] = useState<PatternType | "all">("all");
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-patterns", type, search],
    queryFn: () =>
      list({
        data: {
          pattern_type: type === "all" ? null : type,
          search: search || null,
          limit: 100,
        },
      }),
  });

  const mut = useMutation({
    mutationFn: (input: { pattern_id: string; admin_boosted?: boolean; admin_suppressed?: boolean; admin_notes?: string | null }) =>
      flag({ data: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-patterns"] });
      toast.success("Pattern updated");
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to update"),
  });

  const patterns = data?.patterns ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Pattern type</label>
          <Select value={type} onValueChange={(v) => setType(v as PatternType | "all")}>
            <SelectTrigger className="w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="csv_mapping">CSV mapping</SelectItem>
              <SelectItem value="product_classification">Product classification</SelectItem>
              <SelectItem value="metric_override">Metric override</SelectItem>
              <SelectItem value="threshold_tuning">Threshold tuning</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-1 flex-col gap-1">
          <label className="text-xs text-muted-foreground">Search by key</label>
          <Input
            placeholder="hash fragment…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <SeedDefaultsButton />
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading patterns…</p>}
      {!isLoading && patterns.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No aggregated patterns yet. They appear here once multiple accounts contribute matching
          decisions.
        </div>
      )}

      <div className="space-y-2">
        {patterns.map((p) => {
          const meta = (p as any).latest_meta as { product_name?: string; amount?: number } | null;
          const label =
            meta?.product_name
              ? `${meta.product_name}${typeof meta.amount === "number" ? `, $${meta.amount.toFixed(2)}` : ""}`
              : null;
          const status = p.admin_suppressed
            ? { text: "Suppressed", variant: "destructive" as const }
            : p.admin_boosted
              ? { text: "Boosted (forced active)", variant: "default" as const }
              : p.is_active
                ? { text: "Active", variant: "default" as const }
                : { text: "Inactive (needs ≥2 contributors)", variant: "secondary" as const };
          return (
            <div key={p.id} className="rounded-md border border-border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{p.pattern_type}</Badge>
                <Badge variant={status.variant}>{status.text}</Badge>
                <span className="text-xs text-muted-foreground">
                  {p.contributor_count} contributor{p.contributor_count === 1 ? "" : "s"} · confidence{" "}
                  {Math.round(Number(p.confidence ?? 0) * 100)}% · updated {timeAgo(p.last_updated_at)}
                </span>
              </div>
              {label ? (
                <div className="mt-2 text-sm font-medium">{label}</div>
              ) : (
                <div className="mt-2 text-sm italic text-muted-foreground">
                  No label yet, re-save this classification to attach product name + amount
                </div>
              )}
              <div className="mt-1 break-all font-mono text-[10px] text-muted-foreground/70">
                {p.pattern_key}
              </div>
              <pre className="mt-2 max-h-40 overflow-auto rounded bg-muted/40 p-2 text-xs">
                {JSON.stringify(p.pattern_value, null, 2)}
              </pre>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  variant={p.admin_boosted ? "default" : "outline"}
                  onClick={() =>
                    mut.mutate({ pattern_id: p.id, admin_boosted: !p.admin_boosted })
                  }
                >
                  {p.admin_boosted ? "Unboost" : "Boost"}
                </Button>
                <Button
                  size="sm"
                  variant={p.admin_suppressed ? "destructive" : "outline"}
                  onClick={() =>
                    mut.mutate({ pattern_id: p.id, admin_suppressed: !p.admin_suppressed })
                  }
                >
                  {p.admin_suppressed ? "Unsuppress" : "Suppress"}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function timeAgo(iso: string | null | undefined): string {
  if (!iso) return ",";
  const diff = Date.now() - new Date(iso).getTime();
  if (!isFinite(diff) || diff < 0) return "just now";
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function SeedDefaultsButton() {
  const preview = useServerFn(previewPatternSeeds);
  const seed = useServerFn(seedAdminPatterns);
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["pattern-seeds-preview"],
    queryFn: () => preview(),
    enabled: open,
  });

  const seeds = data?.seeds ?? [];

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const selectableKeys = seeds.filter((s) => s.state !== "suppressed").map((s) => s.pattern_key);
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selected.has(k));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableKeys));
  };

  const run = useMutation({
    mutationFn: () =>
      seed({
        data: { pattern_keys: selected.size > 0 ? Array.from(selected) : undefined },
      }),
    onSuccess: (res) => {
      toast.success(
        `Seeded ${res.inserted} new, boosted ${res.boosted} existing, skipped ${res.skipped}.`,
      );
      qc.invalidateQueries({ queryKey: ["admin-patterns"] });
      refetch();
      setSelected(new Set());
    },
    onError: (e: { message?: string }) => toast.error(e?.message ?? "Seed failed"),
  });

  const stateBadge = (s: typeof seeds[number]["state"]) => {
    switch (s) {
      case "will_insert":
        return <Badge variant="default">New</Badge>;
      case "will_boost":
        return <Badge variant="secondary">Boost existing</Badge>;
      case "already_boosted":
        return <Badge variant="outline">Already boosted</Badge>;
      case "suppressed":
        return <Badge variant="destructive">Suppressed</Badge>;
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          setOpen(true);
          setSelected(new Set());
        }}
      >
        <Sparkles className="mr-2 h-4 w-4" />
        Seed defaults
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Seed default CSV mapping patterns</DialogTitle>
            <DialogDescription>
              Pre-populate cross-account patterns for the most common affiliate platforms. New
              users uploading these exports get an instant mapping instead of waiting for the AI.
              Boosted patterns stay active regardless of contributor count.
            </DialogDescription>
          </DialogHeader>

          {isLoading ? (
            <p className="py-6 text-sm text-muted-foreground">Loading catalog…</p>
          ) : (
            <div className="max-h-[50vh] space-y-2 overflow-auto">
              <div className="flex items-center gap-2 border-b border-border pb-2">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  id="seed-all"
                />
                <label htmlFor="seed-all" className="text-xs text-muted-foreground">
                  Select all eligible ({selectableKeys.length})
                </label>
              </div>
              {seeds.map((s) => {
                const disabled = s.state === "suppressed";
                return (
                  <label
                    key={s.pattern_key}
                    className={`flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 text-sm ${
                      disabled ? "opacity-60" : "hover:bg-muted/40"
                    }`}
                  >
                    <Checkbox
                      checked={selected.has(s.pattern_key)}
                      onCheckedChange={() => toggle(s.pattern_key)}
                      disabled={disabled}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{s.platform}</span>
                        <span className="text-xs text-muted-foreground">{s.layout_label}</span>
                        {stateBadge(s.state)}
                      </div>
                      <p className="mt-0.5 text-xs text-muted-foreground">{s.notes}</p>
                      <p className="mt-1 font-mono text-[10px] text-muted-foreground/70">
                        Headers: {s.headers.join(", ")}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => run.mutate()}
              disabled={run.isPending || isLoading}
            >
              {run.isPending
                ? "Seeding…"
                : selected.size > 0
                  ? `Seed ${selected.size} selected`
                  : "Seed all eligible"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
